import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Zap, Target, BarChart3, Lock } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import paymentService from "../services/payment";
import "./AccountPricing.css";

// Mirror of backend/app/pricing.py. Kept as a static object so the UI can
// render immediately without an extra network hop.
const TIERS = [
  {
    key: "free",
    name: "Free",
    price: 0,
    priceLabel: "$0",
    tagline: "Try it out",
    features: [
      "3 free report uploads",
      "Download CSV/XLSX results",
      "Access to reports you upload",
    ],
    missing: ["Analytics dashboard", "Access to other users' reports"],
    cta: "Current Plan",
  },
  {
    key: "basic",
    name: "Basic",
    price: 10,
    priceLabel: "$10",
    tagline: "For regular users",
    features: [
      "Up to 100 report uploads per year",
      "Unlimited viewing of reports you upload",
      "Download CSV/XLSX results",
      "Email support",
    ],
    missing: ["Analytics dashboard", "Access to other users' reports"],
    cta: "Subscribe to Basic",
  },
  {
    key: "pro",
    name: "Max",
    price: 20,
    priceLabel: "$20",
    tagline: "For power users",
    highlighted: true,
    features: [
      "Unlimited report uploads",
      "Access to every report in the system",
      "Full analytics dashboard",
      "Reporting Entity Groups (committees + candidates)",
      "Priority email support",
    ],
    missing: [],
    cta: "Subscribe to Max",
  },
];

const tierDisplayName = (key) => {
  if (key === "pro") return "Max";
  if (!key) return "Free";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const AccountPricing = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (tierKey) => {
    setLoading(true);
    setError(null);
    try {
      const { checkout_url } = await paymentService.createSubscriptionCheckout(tierKey);
      paymentService.redirectToCheckout(checkout_url);
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      const detail = err.response?.data?.detail;
      setError(
        (detail && typeof detail === "string" ? detail : detail?.message) ||
          "Failed to start checkout. Please try again."
      );
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);
    try {
      const { portal_url } = await paymentService.createPortalSession();
      paymentService.redirectToPortal(portal_url);
    } catch (err) {
      console.error("Failed to open customer portal:", err);
      const detail = err.response?.data?.detail;
      setError(
        (detail && typeof detail === "string" ? detail : detail?.message) ||
          "Failed to open subscription management. Please try again."
      );
      setLoading(false);
    }
  };

  const currentTier = user?.tier || "free";

  return (
    <main className="account-pricing">
      <div className="pricing-header">
        <h1 className="page-title">Choose Your Plan</h1>
        <p className="page-subtitle">
          Simple annual pricing. Cancel any time — you keep access until your paid period ends.
        </p>
        {user && (
          <div className="current-status">
            <span className="status-label">Current Plan:</span>
            <span className="status-value">{tierDisplayName(currentTier)}</span>
          </div>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <section className="pricing-section">
        <div className="pricing-grid">
          {TIERS.map((tier) => {
            const isCurrent = currentTier === tier.key;
            return (
              <div
                key={tier.key}
                className={`price-card ${tier.highlighted ? "pro" : ""} ${
                  isCurrent ? "current" : ""
                }`}
              >
                {tier.highlighted && <div className="popular-badge">Recommended</div>}
                <div className="card-header">
                  <h3>{tier.name}</h3>
                  <p className="card-subtitle">{tier.tagline}</p>
                </div>
                <div className="card-price">
                  <span className="price">{tier.priceLabel}</span>
                  <span className="period">{tier.price === 0 ? "forever" : "/year"}</span>
                </div>
                <ul className="feature-list">
                  {tier.features.map((f) => (
                    <li key={f}>✓ {f}</li>
                  ))}
                  {tier.missing.map((f) => (
                    <li key={f} className="feature-missing">— {f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button className="btn-outline disabled" disabled>
                    Current Plan
                  </button>
                ) : tier.key === "free" ? (
                  <button
                    className="btn-outline"
                    onClick={handleManageSubscription}
                    disabled={loading || !user}
                  >
                    {loading ? "Processing..." : "Downgrade"}
                  </button>
                ) : (
                  <button
                    className={tier.highlighted ? "btn-primary" : "btn-outline"}
                    onClick={() => handleSubscribe(tier.key)}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : tier.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {user && currentTier !== "free" && (
          <div className="manage-subscription">
            <button className="btn-outline" onClick={handleManageSubscription} disabled={loading}>
              Manage Subscription
            </button>
          </div>
        )}
      </section>

      <section className="value-section">
        <h2>Why SHOWME?</h2>
        <div className="value-grid">
          <div className="value-card">
            <div className="value-icon"><Zap size={32} /></div>
            <h3>Save Time</h3>
            <p>Extract a full report in 30 seconds vs 15–30 minutes manually</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><Target size={32} /></div>
            <h3>99.94% Accurate</h3>
            <p>Automated extraction with verified financial reconciliation</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><BarChart3 size={32} /></div>
            <h3>Built-in Analytics</h3>
            <p>Visualize trends, track donors, and analyze spending patterns</p>
          </div>
          <div className="value-card">
            <div className="value-icon"><Lock size={32} /></div>
            <h3>Secure & Private</h3>
            <p>Bank-level encryption and secure cloud storage</p>
          </div>
        </div>
        <div className="learn-more">
          <Link to="/how-it-works" className="btn-outline">
            Learn How It Works →
          </Link>
        </div>
      </section>

      <div className="return-dashboard">
        {user && (
          <Link to="/dashboard" className="btn-outline">
            ← Back to Dashboard
          </Link>
        )}
      </div>
    </main>
  );
};
